import Service, { type Registry as Services, service } from '@ember/service';
import { type Filter } from 'uplisting-frontend/utils/filters/types';
import { FilterId } from 'uplisting-frontend/utils/filters/abstract';
import {
  getPolicyValuesForCategory,
  getQualityStatusValuesForCategory,
  type PolicyCategory,
  type QualityStatusCategory,
} from 'uplisting-frontend/utils/airbnb-quality-dashboard';

export interface IFilterState {
  countries: string[];
  cities: string[];
  airbnbOfficialLinkIds: string[];
  airbnbQualityStates: string[];
  cancellationPolicies: string[];
  tripIssues: 'all' | 'yes' | 'no';
}

export interface IFilterSummary {
  label: string;
  value: string;
}

const FILTER_QUERY_PARAM_MAP: Partial<Record<FilterId, string>> = {
  [FilterId.country]: 'countries',
  [FilterId.city]: 'cities',
  [FilterId.airbnbOfficialConnection]: 'airbnbOfficialConnectionIds',
  [FilterId.airbnbOfficialLink]: 'airbnbOfficialLinkIds',
  [FilterId.airbnbQualityState]: 'airbnbQualityStates',
  [FilterId.airbnbCancellationPolicy]: 'cancellationPolicies',
};

interface IFilterMetadata {
  translationKey: string;
  formatter?: (value: string[]) => string;
}

const FILTER_METADATA: Record<string, IFilterMetadata> = {
  countries: {
    translationKey: 'filters.country.label',
    formatter: (value) => value.join(', '),
  },
  cities: {
    translationKey: 'filters.city.label',
    formatter: (value) => value.join(', '),
  },
  airbnbOfficialConnectionIds: {
    translationKey: 'filters.airbnb_connection.label',
    formatter: (value) => value.join(', '),
  },
  airbnbOfficialLinkIds: {
    translationKey: 'filters.airbnb_link.label',
    formatter: (value) => `${value.length} selected`,
  },
  airbnbQualityStates: {
    translationKey: 'filters.airbnb_quality_state.label',
    formatter: (value) => value.join(', '),
  },
  cancellationPolicies: {
    translationKey: 'filters.airbnb_cancellation_policy.label',
    formatter: (value) => value.join(', '),
  },
};

export default class AirbnbQualityDashboardFiltersService extends Service {
  @service('filters') filtersService!: Services['filters'];
  @service intl!: Services['intl'];

  createFilters(queryParams: {
    countries?: string[];
    cities?: string[];
    airbnbOfficialLinkIds?: string[];
    airbnbOfficialConnectionIds?: string[];
    airbnbQualityStates?: string[];
    cancellationPolicies?: string[];
  }): Filter[] {
    const { filtersService } = this;

    return [
      filtersService.createFilterFor(
        FilterId.country,
        queryParams.countries || [],
      ),
      filtersService.createFilterFor(FilterId.city, queryParams.cities || []),
      filtersService.createFilterFor(
        FilterId.airbnbOfficialConnection,
        queryParams.airbnbOfficialConnectionIds || [],
      ),
      filtersService.createFilterFor(
        FilterId.airbnbOfficialLink,
        queryParams.airbnbOfficialLinkIds || [],
      ),
      filtersService.createFilterFor(
        FilterId.airbnbQualityState,
        queryParams.airbnbQualityStates || [],
      ),
      filtersService.createFilterFor(
        FilterId.airbnbCancellationPolicy,
        queryParams.cancellationPolicies || [],
      ),
    ];
  }

  getLocationFilters(filters: Filter[]): Filter[] {
    return filters.filter(
      (filter) => filter.id === FilterId.country || filter.id === FilterId.city,
    );
  }

  getListingFilters(filters: Filter[]): Filter[] {
    return filters.filter(
      (filter) =>
        filter.id === FilterId.airbnbOfficialLink ||
        filter.id === FilterId.airbnbOfficialConnection,
    );
  }

  getQualityFilters(filters: Filter[]): Filter[] {
    return filters.filter(
      (filter) =>
        filter.id === FilterId.airbnbQualityState ||
        filter.id === FilterId.airbnbCancellationPolicy,
    );
  }

  getAppliedFiltersCount(filters: Filter[], tripIssues?: string): number {
    const arrayCount = filters.filter((filter) => {
      const value = filter.toQuery<string[]>();
      return value && value.length > 0;
    }).length;
    const tripIssuesCount = tripIssues !== undefined ? 1 : 0;
    return arrayCount + tripIssuesCount;
  }

  getAppliedFiltersSummary(
    filters: Filter[],
    tripIssues?: string,
  ): IFilterSummary[] {
    const summary: IFilterSummary[] = [];

    for (const filter of filters) {
      const value = filter.toQuery<string[]>();
      if (!value || value.length === 0) continue;

      const queryParamName = FILTER_QUERY_PARAM_MAP[filter.id];
      if (!queryParamName) continue;

      const metadata = FILTER_METADATA[queryParamName];
      if (metadata) {
        summary.push({
          label: this.intl.t(metadata.translationKey),
          value: metadata.formatter?.(value) ?? '',
        });
      }
    }

    if (tripIssues !== undefined) {
      summary.push({
        label: this.intl.t('filters.trip_issues.label'),
        value:
          tripIssues === 'yes'
            ? this.intl.t('filters.trip_issues.options.yes')
            : this.intl.t('filters.trip_issues.options.no'),
      });
    }

    return summary;
  }

  getQueryParamsForApi(queryParams: {
    countries?: string[];
    cities?: string[];
    airbnbOfficialLinkIds?: string[];
    airbnbOfficialConnectionIds?: string[];
    airbnbQualityStates?: string[];
    cancellationPolicies?: string[];
    tripIssues?: string;
  }): {
    countries?: string[];
    cities?: string[];
    airbnb_official_connection_ids?: string[];
    airbnb_official_link_ids?: number[];
    quality_statuses?: string[];
    cancellation_policies?: string[];
    trip_issues?: boolean;
  } {
    const linkIds = (queryParams.airbnbOfficialLinkIds || [])
      .map((id) => parseInt(id, 10))
      .filter((id) => !isNaN(id));

    // Map normalized cancellation policy values to backend values for API request
    const apiCancellationPolicies =
      queryParams.cancellationPolicies &&
      queryParams.cancellationPolicies.length > 0
        ? queryParams.cancellationPolicies.flatMap((value) =>
            getPolicyValuesForCategory(value as PolicyCategory),
          )
        : undefined;

    const apiQualityStatuses =
      queryParams.airbnbQualityStates &&
      queryParams.airbnbQualityStates.length > 0
        ? queryParams.airbnbQualityStates.flatMap((value) =>
            getQualityStatusValuesForCategory(value as QualityStatusCategory),
          )
        : undefined;

    let tripIssuesBool: boolean | undefined = undefined;
    if (queryParams.tripIssues === 'yes') {
      tripIssuesBool = true;
    } else if (queryParams.tripIssues === 'no') {
      tripIssuesBool = false;
    }

    return {
      countries:
        queryParams.countries && queryParams.countries.length > 0
          ? queryParams.countries
          : undefined,
      cities:
        queryParams.cities && queryParams.cities.length > 0
          ? queryParams.cities
          : undefined,
      airbnb_official_link_ids: linkIds.length > 0 ? linkIds : undefined,
      airbnb_official_connection_ids:
        queryParams.airbnbOfficialConnectionIds &&
        queryParams.airbnbOfficialConnectionIds.length > 0
          ? queryParams.airbnbOfficialConnectionIds
          : undefined,
      quality_statuses: apiQualityStatuses,
      cancellation_policies: apiCancellationPolicies,
      trip_issues: tripIssuesBool,
    };
  }
}

declare module '@ember/service' {
  interface Registry {
    'airbnb-quality-dashboard-filters': AirbnbQualityDashboardFiltersService;
  }
}
